/**
 * Refresh Manager
 *
 * State machine for coordinating data refresh operations after wake from sleep
 * or network reconnection. Handles:
 * - Network readiness verification (not just navigator.onLine)
 * - Debouncing to prevent rapid-fire refreshes
 * - Exponential backoff retry logic
 * - Cooldown between refreshes
 * - Wake-from-sleep detection via visibility change duration
 *
 * State Flow:
 * IDLE → WAITING_FOR_NETWORK → DEBOUNCING → REFRESHING → COOLING_DOWN → IDLE
 */

import { isNetworkError, probeNetworkReady, getBackoffDelay, sleep, NETWORK_CONFIG } from './networkUtils'

export const REFRESH_STATE = {
  IDLE: 'IDLE',
  WAITING_FOR_NETWORK: 'WAITING_FOR_NETWORK',
  DEBOUNCING: 'DEBOUNCING',
  REFRESHING: 'REFRESHING',
  COOLING_DOWN: 'COOLING_DOWN'
}

// Export class for testing (use refreshManager singleton for app code)
export class RefreshManager {
  constructor() {
    // State machine
    this.state = REFRESH_STATE.IDLE
    this.listeners = new Set()

    // Timing tracking
    this.lastHiddenTime = null
    this.lastRefreshTime = null

    // Cancellation support
    this.abortController = null

    // Configuration (uses shared NETWORK_CONFIG where applicable)
    this.config = {
      DEBOUNCE_MS: 10000,                         // Wait 10s for network to stabilize after wake (DNS/TCP needs time)
      COOLDOWN_MS: 30000,                         // 30s minimum between refreshes
      MIN_HIDDEN_MS: 10000,                       // Only refresh if hidden 10+ seconds (wake-from-sleep)
      MAX_RETRIES: 3,                             // Maximum retry attempts
      PROBE_TIMEOUT_MS: NETWORK_CONFIG.PROBE_TIMEOUT_MS  // Use shared config
    }
  }

  /**
   * Transition to a new state and notify listeners
   */
  setState(newState) {
    const oldState = this.state
    this.state = newState
    console.log(`[RefreshManager] State: ${oldState} → ${newState}`)
    this.listeners.forEach(fn => {
      try {
        fn(newState)
      } catch (err) {
        console.error('[RefreshManager] Listener error:', err)
      }
    })
  }

  /**
   * Subscribe to state changes
   * @param {Function} listener - Called with new state on each transition
   * @returns {Function} Unsubscribe function
   */
  subscribe(listener) {
    this.listeners.add(listener)
    // Immediately notify with current state
    listener(this.state)
    return () => this.listeners.delete(listener)
  }

  /**
   * Track when page becomes hidden (for wake-from-sleep detection)
   */
  onVisibilityHidden() {
    this.lastHiddenTime = Date.now()
    console.log('[RefreshManager] Page hidden, tracking time')
  }

  /**
   * Request a data refresh with state machine coordination
   *
   * Flow: IDLE → WAITING_FOR_NETWORK → DEBOUNCING → REFRESHING → COOLING_DOWN → IDLE
   *
   * Guards:
   * - Ignores request if not in IDLE state (refresh already in progress)
   * - Ignores request if cooldown is active (30s between refreshes)
   * - For visibility changes, skips if hidden < 10s (not wake-from-sleep)
   *
   * @param {Function} refreshCallback - Async function that performs the data refresh
   * @param {Object} options - Configuration options
   * @param {string} options.reason - Why refresh is requested ('visibility-change', 'network-online', 'realtime-reconnection')
   * @param {string} options.supabaseUrl - Supabase URL for network connectivity probing
   * @param {Function} [options.onError] - Optional callback called when refresh fails after all retries
   * @returns {Promise<void>} Resolves when refresh cycle completes (or is skipped)
   *
   * @example
   * refreshManager.requestRefresh(
   *   async () => {
   *     const result = await dispatch(fetchBootstrapData(userId))
   *     dispatch(setTasks(result.payload.tasks))
   *   },
   *   {
   *     reason: 'visibility-change',
   *     supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
   *     onError: (error) => dispatch(addNotification({ type: 'error', message: 'Failed to sync' }))
   *   }
   * )
   */
  async requestRefresh(refreshCallback, { reason = 'unknown', supabaseUrl, onError } = {}) {
    // Guard: Already in non-idle state (refresh in progress)
    if (this.state !== REFRESH_STATE.IDLE) {
      console.log(`[RefreshManager] Ignoring request (${reason}), state is ${this.state}`)
      return
    }

    // Guard: Check cooldown
    if (this.lastRefreshTime) {
      const timeSinceRefresh = Date.now() - this.lastRefreshTime
      if (timeSinceRefresh < this.config.COOLDOWN_MS) {
        console.log(`[RefreshManager] Cooldown active, ${Math.ceil((this.config.COOLDOWN_MS - timeSinceRefresh) / 1000)}s remaining`)
        return
      }
    }

    // Guard: For visibility changes, only refresh if hidden long enough
    if (reason === 'visibility-change' && this.lastHiddenTime) {
      const hiddenMs = Date.now() - this.lastHiddenTime
      if (hiddenMs < this.config.MIN_HIDDEN_MS) {
        console.log(`[RefreshManager] Hidden only ${hiddenMs}ms, skipping (need ${this.config.MIN_HIDDEN_MS}ms)`)
        return
      }
      console.log(`[RefreshManager] Was hidden for ${hiddenMs}ms, proceeding with refresh`)
    }

    console.log(`[RefreshManager] Refresh requested: ${reason}`)

    // Create abort controller for this refresh cycle
    this.abortController = new AbortController()

    try {
      // Phase 1: Wait for network to be ready
      this.setState(REFRESH_STATE.WAITING_FOR_NETWORK)
      const networkReady = await this.waitForNetwork(supabaseUrl)

      if (this.abortController?.signal.aborted) {
        this.setState(REFRESH_STATE.IDLE)
        return
      }

      if (!networkReady) {
        console.log('[RefreshManager] Network not available after retries')
        this.setState(REFRESH_STATE.IDLE)
        return
      }

      // Phase 2: Debounce (wait for connection to stabilize)
      this.setState(REFRESH_STATE.DEBOUNCING)
      await sleep(this.config.DEBOUNCE_MS)

      if (this.abortController?.signal.aborted) {
        this.setState(REFRESH_STATE.IDLE)
        return
      }

      // Phase 3: Execute refresh with retries
      this.setState(REFRESH_STATE.REFRESHING)
      const refreshSucceeded = await this.executeWithRetry(refreshCallback)

      if (this.abortController?.signal.aborted) {
        this.setState(REFRESH_STATE.IDLE)
        return
      }

      // Only record refresh time and enter cooldown if refresh actually succeeded
      // This prevents failed refreshes from blocking retry attempts
      if (!refreshSucceeded) {
        console.log('[RefreshManager] Refresh failed, skipping cooldown')
        // Notify caller of failure so they can show user feedback
        if (onError) {
          try {
            onError(new Error('Refresh failed after retries'))
          } catch (callbackErr) {
            console.error('[RefreshManager] onError callback threw:', callbackErr)
          }
        }
        this.setState(REFRESH_STATE.IDLE)
        return
      }

      // Record successful refresh time
      this.lastRefreshTime = Date.now()

      // Phase 4: Enter cooldown
      this.setState(REFRESH_STATE.COOLING_DOWN)
      await sleep(this.config.COOLDOWN_MS)

      if (this.abortController?.signal.aborted) {
        this.setState(REFRESH_STATE.IDLE)
        return
      }

      this.setState(REFRESH_STATE.IDLE)

    } catch (err) {
      console.error('[RefreshManager] Unexpected error:', err)
      this.setState(REFRESH_STATE.IDLE)
    } finally {
      this.abortController = null
    }
  }

  /**
   * Wait for network to be ready with retries
   */
  async waitForNetwork(supabaseUrl) {
    for (let attempt = 0; attempt < this.config.MAX_RETRIES; attempt++) {
      if (this.abortController?.signal.aborted) return false

      console.log(`[RefreshManager] Network probe attempt ${attempt + 1}/${this.config.MAX_RETRIES}`)

      const ready = await probeNetworkReady(supabaseUrl, this.config.PROBE_TIMEOUT_MS)
      if (ready) {
        console.log('[RefreshManager] Network is ready')
        return true
      }

      // Wait before next attempt (except on last attempt)
      if (attempt < this.config.MAX_RETRIES - 1) {
        const delay = getBackoffDelay(attempt)
        console.log(`[RefreshManager] Network not ready, retrying in ${Math.round(delay)}ms`)
        await sleep(delay)
      }
    }

    return false
  }

  /**
   * Execute refresh callback with retry logic
   * @returns {Promise<boolean>} True if refresh succeeded, false otherwise
   */
  async executeWithRetry(callback) {
    for (let attempt = 0; attempt < this.config.MAX_RETRIES; attempt++) {
      if (this.abortController?.signal.aborted) return false

      try {
        console.log(`[RefreshManager] Refresh attempt ${attempt + 1}/${this.config.MAX_RETRIES}`)
        await callback()
        console.log('[RefreshManager] Refresh succeeded')
        return true // Success
      } catch (err) {
        const isNetwork = isNetworkError(err)

        if (isNetwork && attempt < this.config.MAX_RETRIES - 1) {
          // Network error, will retry
          const delay = getBackoffDelay(attempt)
          console.log(`[RefreshManager] Network error, retrying in ${Math.round(delay)}ms:`, err?.message)
          await sleep(delay)

          // Check if cancelled during sleep
          if (this.abortController?.signal.aborted) return false
        } else if (isNetwork) {
          // Network error, max retries reached
          console.log('[RefreshManager] Max retries reached for network error')
          return false
        } else {
          // Non-network error, don't retry
          console.error('[RefreshManager] Non-network error, not retrying:', err)
          return false
        }
      }
    }
    return false
  }

  /**
   * Cancel any pending refresh operation
   *
   * Aborts in-flight network requests and resets state to IDLE.
   * Safe to call even if no refresh is in progress.
   *
   * @returns {void}
   */
  cancel() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    this.setState(REFRESH_STATE.IDLE)
    console.log('[RefreshManager] Cancelled')
  }

  /**
   * Get current state for debugging or UI display
   *
   * @returns {Object} Current state info
   * @returns {string} returns.state - Current state machine state (IDLE, WAITING_FOR_NETWORK, etc.)
   * @returns {number|null} returns.lastHiddenTime - Timestamp when page was last hidden
   * @returns {number|null} returns.lastRefreshTime - Timestamp of last successful refresh
   * @returns {number} returns.cooldownRemaining - Milliseconds remaining in cooldown (0 if not in cooldown)
   */
  getState() {
    return {
      state: this.state,
      lastHiddenTime: this.lastHiddenTime,
      lastRefreshTime: this.lastRefreshTime,
      cooldownRemaining: this.lastRefreshTime
        ? Math.max(0, this.config.COOLDOWN_MS - (Date.now() - this.lastRefreshTime))
        : 0
    }
  }

  /**
   * Reset the manager to initial state
   *
   * Cancels any pending refresh, clears timing state.
   * Useful for testing or when user logs out.
   *
   * @returns {void}
   */
  reset() {
    this.cancel()
    this.lastHiddenTime = null
    this.lastRefreshTime = null
  }
}

// Export singleton instance
export const refreshManager = new RefreshManager()
